/**
 * 🏠 Dashboard de Status da API
 * 
 * Componente de demonstração que mostra a integração frontend-backend funcionando
 * Exibe informações em tempo real da nossa API REST
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { 
  Server, 
  Users, 
  Calendar, 
  Building, 
  Handshake, 
  CheckCircle, 
  XCircle, 
  Loader2,
  RefreshCw,
  Database,
  Clock,
  Globe
} from 'lucide-react';

import { 
  useApiInfo, 
  useApiStatus, 
  useEvents, 
  useUsers, 
  useParticipants, 
  useMeetings,
  useClearCache 
} from '@/hooks/useApi';

export default function ApiDashboard() {
  const { data: apiInfo, isLoading: infoLoading } = useApiInfo();
  const { isConnected, isLoading: statusLoading, health } = useApiStatus();
  const { data: events, isLoading: eventsLoading } = useEvents();
  const { data: users, isLoading: usersLoading } = useUsers();
  const { data: participants, isLoading: participantsLoading } = useParticipants();
  const { data: meetings, isLoading: meetingsLoading } = useMeetings();
  const { clearAll } = useClearCache();

  const handleClearCache = () => {
    clearAll();
    window.location.reload(); // Forçar reload para mostrar dados frescos
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Dashboard</h1>
          <p className="text-muted-foreground">
            Status da integração Frontend ↔ Backend
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleClearCache}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Limpar Cache
          </Button>
          
          <Badge variant={isConnected ? "default" : "destructive"}>
            {statusLoading ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : isConnected ? (
              <CheckCircle className="h-3 w-3 mr-1" />
            ) : (
              <XCircle className="h-3 w-3 mr-1" />
            )}
            {statusLoading ? 'Verificando...' : isConnected ? 'Online' : 'Offline'}
          </Badge>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* API Status */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Status da API
            </CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statusLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : isConnected ? (
                <span className="text-green-600">Ativa</span>
              ) : (
                <span className="text-red-600">Inativa</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {health ? `Uptime: ${Math.round(health.uptime)}s` : 'Sem dados'}
            </p>
          </CardContent>
        </Card>

        {/* Events Count */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Eventos
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {eventsLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                events?.length || 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Total de eventos cadastrados
            </p>
          </CardContent>
        </Card>

        {/* Users Count */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Usuários
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {usersLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                users?.length || 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Total de usuários ativos
            </p>
          </CardContent>
        </Card>

        {/* Participants Count */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Participantes
            </CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {participantsLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                participants?.length || 0
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Empresas cadastradas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* API Information */}
      {apiInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Informações da API
            </CardTitle>
            <CardDescription>
              Detalhes da API REST do B2B Meet Flow
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium">Nome</p>
                <p className="text-sm text-muted-foreground">{apiInfo.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Versão</p>
                <p className="text-sm text-muted-foreground">{apiInfo.version}</p>
              </div>
              <div>
                <p className="text-sm font-medium">Ambiente</p>
                <Badge variant="outline">
                  {health?.env || 'development'}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Endpoints */}
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Database className="h-4 w-4" />
                Endpoints Disponíveis
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(apiInfo.endpoints || {}).map(([key, endpoint]: [string, any]) => (
                  <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="text-sm font-medium capitalize">{endpoint.description}</p>
                      <p className="text-xs text-muted-foreground">{endpoint.base}</p>
                    </div>
                    <div className="flex gap-1">
                      {endpoint.methods?.map((method: string) => (
                        <Badge key={method} variant="secondary" className="text-xs">
                          {method}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Authentication Info */}
            {apiInfo.authentication && (
              <div>
                <h4 className="text-sm font-medium mb-3">Autenticação</h4>
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-sm">
                    <strong>Tipo:</strong> {apiInfo.authentication.type}
                  </p>
                  <p className="text-sm">
                    <strong>Header:</strong> {apiInfo.authentication.header}
                  </p>
                  <p className="text-sm">
                    <strong>Formato:</strong> <code>{apiInfo.authentication.format}</code>
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Real-time Data Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Events Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Últimos Eventos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {eventsLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Carregando eventos...</span>
              </div>
            ) : events && events.length > 0 ? (
              <div className="space-y-2">
                {events.slice(0, 3).map((event) => (
                  <div key={event.id} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <p className="text-sm font-medium">{event.nome}</p>
                      <p className="text-xs text-muted-foreground">
                        Criado em {new Date(event.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <Badge variant="outline">
                      {event.diasEvento?.length || 0} dias
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum evento encontrado</p>
            )}
          </CardContent>
        </Card>

        {/* Users Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usuários Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Carregando usuários...</span>
              </div>
            ) : users && users.length > 0 ? (
              <div className="space-y-2">
                {users.slice(0, 3).map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <p className="text-sm font-medium">{user.nome}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <Badge variant="outline">
                      {user.papel}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum usuário encontrado</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Connection Status Footer */}
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            {isConnected ? (
              <span>
                Conectado ao servidor em <strong>http://localhost:3001</strong> 
                {health && ` • Porta: ${health.port}`}
              </span>
            ) : (
              <span>
                Desconectado do servidor • Verifique se a API está rodando
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}